// api/appendice.ts — FE client for the per-argomento Appendice THEORY chapter.
//
// Backend: `GET /api/argomenti/:id/chapter` (handler
// `ai-tutor/backend/src/gateway/appendice-chapter.ts`, mounted in index.ts).
// The chapter is a build-time-frozen theory asset MERGED with the LIVE DB
// principi, so the regulated "key rule" text never goes stale (spec p3 §0.2 H4).
//
// This is the missing FE half of P3 — the backend has served the full theory
// since commit e30e97a, but no screen consumed it (the Appendice showed only
// the per-principle enunciato + trappole). R2-2 wires the full chapter theory
// into the Appendice as an argomento-level "Teoria" view.
//
// Shapes mirror the served `AppendiceChapterPayload` contract from
// appendice-chapter.ts. NOTE on `theory.contenuto`: the backend types it as a
// `string`, but the frozen asset stores it as an ARRAY of strings (paragraphs)
// and `mergeChapter` passes it through unchanged — so the live payload can be
// EITHER. The render layer normalizes both forms (see ChapterTheoryView).

import { apiJson } from './client';

/** One per-principle "key rule" woven into the chapter (live text, frozen meta). */
export interface ChapterKeyRule {
  /** Full DB principle id (`argN_PK`) → /appendice/:argomentoId/:principleId. */
  principle_id: string;
  name_it: string;
  /** LIVE from `principles.enunciato`. */
  factual: string;
  /** LIVE from `principles.regola` (nullable). */
  rule: string | null;
  /** Principle-level images only (spec §0.2 H1). */
  images: string[];
  /** Exam-frequency hint from the asset (build-time, stable). */
  exam_questions: number;
}

export interface ChapterSubSectionImage {
  ref?: string;
  path?: string;
  hash?: string;
  exists?: boolean;
  description?: {
    italianName?: string;
    meaning?: string;
    glyph?: string;
    category?: string;
  };
}

/** One tiered theory sub-section (frozen paragraphs + VERO/FALSO quiz readings). */
export interface ChapterSubSection {
  num: string;
  sezione: number;
  sub: number;
  title: string;
  /** "core" | "approfondimenti" — collapsed-tail tiering. */
  tier: string;
  exam_frequency: number;
  matched_principle_ids: string[];
  theory: {
    source_url?: string;
    source_description?: string;
    /** Frozen theory paragraphs. May be a single string OR an array of strings. */
    contenuto: string | string[];
    /** Appendice-textbook campaign: gated reference prose — renders FIRST when
     *  present (the legacy summary bullets are then suppressed). */
    capitolo_md?: string;
    /** ✅ VERO nei quiz. */
    letture_confermate: string[];
    /** ❌ FALSO nei quiz — MUST be framed as false (spec §0.2 H3). */
    letture_da_scartare: string[];
  };
  images?: ChapterSubSectionImage[];
}

/** The structured "in breve" (Prof) layer — founder-reviewed templated copy. */
export interface ChapterProfLayer {
  titolo?: string;
  intro_template?: string;
  punti_chiave?: Array<{
    principle_id: string;
    name_it: string;
    takeaway: string;
  }>;
  prof_layer_status?: string;
  generation_method?: string;
}

export interface AppendiceChapter {
  argomento_id: string;
  sezione: number;
  /** Full title (NOT the truncated `argomento_name`). */
  sezione_title: string;
  argomento_label: string;
  /** Structured top-8 "in breve" object (build-time, founder-reviewed copy). */
  prof_layer: ChapterProfLayer | null;
  /** Appendice-textbook campaign: chapter intro prose (editorial spine). */
  capitolo_intro_md?: string;
  /** Appendice-textbook campaign: chapter recap (summary tables). */
  capitolo_recap_md?: string;
  key_rules: ChapterKeyRule[];
  sub_sections: ChapterSubSection[];
  stats: unknown;
  needs_review: unknown[];
  superseded_dropped: number;
}

// Item A (2026-06-11) — per-argomento chapter client cache. The AppendiceTheory
// screen (`/appendice/:id/teoria`) and the PrincipleDetail screen
// (`/appendice/:id/:principleId`) both need the SAME merged chapter (theory body
// inline). A module-level promise cache keyed by argomento id means visiting the
// detail page after the teoria page (or paging between sibling principles) does
// NOT re-fetch the chapter. The cache holds the in-flight PROMISE (not just the
// resolved value) so two near-simultaneous callers share one request; a rejected
// fetch is evicted so a later retry can succeed.
const chapterCache = new Map<string, Promise<AppendiceChapter>>();

export const appendiceApi = {
  /** Fetch the merged theory chapter for one argomento (`arg1`..`arg25`).
   *  Shares a per-argomento client cache with `getChapterCached` so repeat
   *  reads within a session don't re-hit the network. */
  async getChapter(argomentoId: string): Promise<AppendiceChapter> {
    const res = await apiJson<{ chapter: AppendiceChapter }>(
      `/api/argomenti/${argomentoId}/chapter`,
    );
    return res.chapter;
  },

  /** Cached variant — returns the shared in-flight/resolved chapter promise for
   *  this argomento. A rejected fetch is evicted so the next call retries. */
  getChapterCached(argomentoId: string): Promise<AppendiceChapter> {
    const cached = chapterCache.get(argomentoId);
    if (cached) return cached;
    const p = this.getChapter(argomentoId).catch((err) => {
      chapterCache.delete(argomentoId);
      throw err;
    });
    chapterCache.set(argomentoId, p);
    return p;
  },
};

/**
 * Item A — bridge a principle id to the chapter sub-sections it belongs to,
 * reusing the FP6 QuestionBrowser pattern: `matched_principle_ids` may be SHORT
 * ("P1") or FULL ("arg1_P1"), so we normalize both ways before comparing. A
 * principle can match more than one sub-section; all matches are returned in the
 * chapter's natural (document) order so the inline theory reads top-to-bottom.
 */
export function subSectionsForPrinciple(
  chapter: AppendiceChapter | null,
  argomentoId: string,
  principleId: string,
): ChapterSubSection[] {
  if (!chapter || !principleId) return [];
  return chapter.sub_sections.filter((s) =>
    s.matched_principle_ids.some((pid) => principleIdMatches(pid, argomentoId, principleId)),
  );
}

/**
 * Appendice restructure (2026-07-14): the SHARED short/full principle-id
 * matcher — ids may arrive SHORT ("P1") or FULL ("arg1_P1") depending on the
 * source (chapter asset vs KB rows). Used to bridge chapter sub-sections,
 * question `principle_ids`, and per-principle proofs to a principle page.
 */
export function principleIdMatches(pid: string, argomentoId: string, principleId: string): boolean {
  const full = pid.startsWith(`${argomentoId}_`) ? pid : `${argomentoId}_${pid}`;
  return full === principleId || pid === principleId;
}

/** Normalize `theory.contenuto` (string OR string[] from the frozen asset) to a
 *  single markdown blob (paragraphs joined by blank lines) for ProfProse. */
export function chapterContenutoToText(contenuto: string | string[] | undefined): string {
  if (!contenuto) return '';
  if (Array.isArray(contenuto)) return contenuto.filter(Boolean).join('\n\n');
  return contenuto;
}
